// src/utils/markdown.js
//
// Markdown muy ligero (soporta encabezados, negritas, cursivas, listas,
// código, tablas). Suficiente para textos de curso creados con el seed
// inicial y migrados al editor WYSIWYG. Pensado para renderizar en el
// aula del estudiante y para precargar el editor cuando el valor en BD
// aun esta en Markdown.
//
// isHtmlLike(value) detecta si un string ya es HTML (ha pasado por el
// editor rico) para no re-parsearlo.

export function isHtmlLike(value) {
  if (!value) return false;
  const trimmed = value.trim();
  // Heurística: empieza con un tag HTML común
  return /^<(h\d|p|ul|ol|div|table|pre|blockquote|strong|em|span|br)[\s>]/i.test(trimmed);
}

export function markdownToHtml(md) {
  if (!md) return '';
  const esc = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const lines = md.split('\n');
  const out = [];
  let inList = false;
  let inCode = false;
  let codeBuf = [];
  let tableBuf = [];

  const flushList = () => { if (inList) { out.push('</ul>'); inList = false; } };
  const flushTable = () => {
    if (tableBuf.length === 0) return;
    const rows = tableBuf.map(r =>
      r.split('|').map(c => c.trim()).filter((c, i, a) => !(i === 0 && c === '') && !(i === a.length - 1 && c === ''))
    );
    if (rows.length >= 2 && rows[1].every(c => /^-+$/.test(c))) {
      const head = rows[0];
      const body = rows.slice(2);
      out.push('<table>');
      out.push('<thead><tr>' + head.map(c => `<th>${esc(c)}</th>`).join('') + '</tr></thead>');
      out.push('<tbody>' + body.map(r => '<tr>' + r.map(c => `<td>${esc(c)}</td>`).join('') + '</tr>').join('') + '</tbody>');
      out.push('</table>');
    } else {
      out.push('<p>' + tableBuf.map(esc).join('<br/>') + '</p>');
    }
    tableBuf = [];
  };

  const inlineMd = (s) => s
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/\*([^*]+)\*/g, '<em>$1</em>')
    .replace(/`([^`]+)`/g, '<code>$1</code>');

  for (const raw of lines) {
    const line = raw;

    // Fenced code
    if (line.trim().startsWith('```')) {
      if (inCode) {
        out.push('<pre><code>' + esc(codeBuf.join('\n')) + '</code></pre>');
        codeBuf = []; inCode = false;
      } else {
        flushList(); flushTable();
        inCode = true;
      }
      continue;
    }
    if (inCode) { codeBuf.push(line); continue; }

    // Table
    if (/^\s*\|.*\|\s*$/.test(line)) { flushList(); tableBuf.push(line.trim()); continue; }
    if (tableBuf.length > 0) flushTable();

    // Headings
    const h = /^(#{1,4})\s+(.*)$/.exec(line);
    if (h) {
      flushList();
      const lvl = h[1].length;
      out.push(`<h${lvl}>${inlineMd(esc(h[2]))}</h${lvl}>`);
      continue;
    }

    // Lists
    if (/^\s*[-*]\s+/.test(line)) {
      if (!inList) { out.push('<ul>'); inList = true; }
      out.push('<li>' + inlineMd(esc(line.replace(/^\s*[-*]\s+/, ''))) + '</li>');
      continue;
    } else if (inList && line.trim() === '') {
      flushList();
      continue;
    }

    // Blank / paragraph
    if (line.trim() === '') { out.push(''); continue; }
    out.push('<p>' + inlineMd(esc(line)) + '</p>');
  }

  flushList();
  flushTable();
  if (inCode) out.push('<pre><code>' + esc(codeBuf.join('\n')) + '</code></pre>');

  return out.join('\n');
}

// Devuelve siempre HTML. Si ya era HTML, lo deja igual. Si era Markdown,
// lo convierte. Útil para precargar el editor sin preocuparse del origen
// del valor.
export function toHtml(value) {
  if (!value) return '';
  return isHtmlLike(value) ? value : markdownToHtml(value);
}
